import { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

// Componente para gerenciar a sessão entre abas
const SessionManager = () => {
  const { user, checkAndRefreshToken } = useAuth();
  const lastFocusTime = useRef(Date.now());
  const lastActivityTime = useRef(Date.now());
  const checkInProgress = useRef(false);

  // Função para atualizar a última atividade do usuário
  const updateLastActivity = () => {
    try {
      const now = Date.now();
      lastActivityTime.current = now;
      
      const savedSession = localStorage.getItem('userSession');
      if (savedSession) {
        const sessionData = JSON.parse(savedSession);
        sessionData.lastActive = new Date().toISOString();
        localStorage.setItem('userSession', JSON.stringify(sessionData));
      }
    } catch (error) {
      console.error('Erro ao atualizar última atividade:', error);
    }
  };

  // Verificar e atualizar a sessão quando a aba recebe foco
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Verificar se passou pelo menos 30 segundos desde o último foco
        // para evitar verificações frequentes ao alternar rapidamente entre abas
        const now = Date.now();
        if (now - lastFocusTime.current > 30000 && !checkInProgress.current) { // 30 segundos
          // Apenas para depuração, remover em produção
          if (process.env.NODE_ENV !== 'production') {
            console.log('Aba recebeu foco, verificando sessão...');
          }
          
          checkInProgress.current = true;
          
          if (user) {
            // Usar Promise para garantir que a verificação termine antes de atualizar flags
            checkAndRefreshToken(true, false) // Adicionado false para não forçar logout em outras abas
              .then(() => {
                updateLastActivity();
                checkInProgress.current = false;
              })
              .catch(() => {
                checkInProgress.current = false;
              });
          } else {
            checkInProgress.current = false;
          }
          
          lastFocusTime.current = now;
        }
      }
    };

    // Adicionar listener para mudanças de visibilidade
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Verificar sessão a cada 10 minutos para manter ativa
    const sessionInterval = setInterval(() => {
      if (user && !checkInProgress.current) {
        checkInProgress.current = true;
        checkAndRefreshToken(true, false) // Adicionado false para não forçar logout em outras abas
          .then(() => {
            checkInProgress.current = false;
          })
          .catch(() => {
            checkInProgress.current = false;
          });
      }
    }, 10 * 60 * 1000); // 10 minutos

    // Atualizar a última atividade a cada 5 minutos se o usuário estiver ativo
    const activityInterval = setInterval(() => {
      const now = Date.now();
      // Se houve atividade nos últimos 5 minutos
      if (now - lastActivityTime.current < 5 * 60 * 1000) {
        updateLastActivity();
      }
    }, 5 * 60 * 1000); // 5 minutos

    // Monitorar atividade do usuário
    const handleUserActivity = () => {
      lastActivityTime.current = Date.now();
    };

    // Adicionar listeners para eventos de atividade do usuário
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('click', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      clearInterval(sessionInterval);
      clearInterval(activityInterval);
    };
  }, [user, checkAndRefreshToken]);

  // Este componente não renderiza nada
  return null;
};

export default SessionManager;